// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = (marketplaceRootURL) => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  if (marketplaceRootURL) {
    return marketplaceRootURL.replace(/\/$/, '');
  }
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  // Server-side rendering: no window/origin to read from, fall back to the
  // configured marketplace root URL (same env var server/api-util/rootURL.js
  // uses for the same purpose on the Express side).
  const envRootURL = process.env.REACT_APP_MARKETPLACE_ROOT_URL;
  return envRootURL ? envRootURL.replace(/\/$/, '') : '';
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: (v) => new sdkTypes.BigDecimal(v.toString()),
    reader: (v) => new Decimal(v.value),
  },
];

const serialize = (data) => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = (str) => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// The native session's access-token cookie (cf_session) is short-lived
// (30 min, see server/api-util/nativeSessionCookies.js) and is meant to be
// silently renewed via the longer-lived refresh cookie (cf_refresh, 30
// days) through POST /api/auth/native/refresh -- but nothing in the client
// ever called that endpoint. So 30 minutes into any native session, the
// very next API call 401s (requireAuthUser.js's native check fails and
// falls through to a Sharetribe SDK call that always fails now, since
// those credentials are revoked), currentUser gets cleared, and the user
// is silently bounced to /signup mid-session. Confirmed live: a logged-in
// user's "Book Now" click 401'd on fetchCurrentUser and redirected to
// signup after ~30+ min of an otherwise-idle-but-open session. One
// in-flight refresh call is shared across concurrent 401s so a burst of
// requests doesn't fire a burst of refreshes.
let nativeRefreshInFlight = null;
const attemptNativeSessionRefresh = () => {
  if (!nativeRefreshInFlight) {
    nativeRefreshInFlight = fetch(`${apiBaseUrl()}/api/auth/native/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
      .then((res) => res.ok)
      .catch(() => false)
      .finally(() => {
        nativeRefreshInFlight = null;
      });
  }
  return nativeRefreshInFlight;
};

const throwResponseError = (res) => {
  return res.json().then((data) => {
    let e = new Error();
    e = Object.assign(e, data);
    e.status = res.status;

    throw e;
  });
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}, isRetry = false) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody
    ? { body: serialize(body) }
    : body !== undefined && body !== null
      ? { body }
      : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return fetch(url, fetchOptions).then((res) => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status === 401 && !isRetry && path !== '/api/auth/native/refresh') {
      return attemptNativeSessionRefresh().then((refreshed) =>
        refreshed ? request(path, options, true) : throwResponseError(res)
      );
    }
    if (res.status >= 400) {
      return throwResponseError(res);
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);

};

const get = (path, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.GET,
  };

  return request(path, requestOptions);
};

const put = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.PUT,
    body,
  };

  return request(path, requestOptions);
};

const patch = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.PATCH,
    body,
  };

  return request(path, requestOptions);
};

const del = (path, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.DELETE,
  };

  return request(path, requestOptions);
};

// `post`/`patch` above only auto-serialize the body when NO headers are
// passed (defaults to transit+json) or when headers explicitly request
// 'application/transit+json' -- any other headers object (e.g. one that
// just sets Accept, as several call sites in this file do) skips
// serialization entirely and hands fetch() a raw JS object. fetch()
// doesn't throw on that; it silently coerces it to the literal string
// "[object Object]", which an express.json()-parsed route then reads as
// an empty body. These wrap that failure mode shut for any route that
// expects real JSON.
const postJson = (path, body, extraHeaders = {}) =>
  post(path, JSON.stringify(body ?? {}), {
    headers: { ...extraHeaders, 'Content-Type': 'application/json' },
  });

const patchJson = (path, body, extraHeaders = {}) =>
  patch(path, JSON.stringify(body ?? {}), {
    headers: { ...extraHeaders, 'Content-Type': 'application/json' },
  });

const getDevUserIdOverride = () => {
  if (process.env.NODE_ENV === 'production') return null;
  if (typeof window !== 'undefined' && window.localStorage) {
    const stored = window.localStorage.getItem('x-dev-user-id') || window.localStorage.getItem('devUserId');
    if (stored && stored.trim()) return stored.trim();
  }
  const envValue = process.env.REACT_APP_DEV_USER_ID;
  return envValue && envValue.trim() ? envValue.trim() : null;
};

const withDevUserHeader = headers => {
  const devUserId = getDevUserIdOverride();
  if (!devUserId) return headers;
  return {
    ...(headers || {}),
    'x-dev-user-id': devUserId,
  };
};

const createQueryString = params => {
  if (!params || typeof params !== 'object') return '';
  const searchParams = new URLSearchParams();
  Object.keys(params).forEach(key => {
    const value = params[key];
    if (value === undefined || value === null || value === '') return;
    searchParams.append(key, String(value));
  });
  const asString = searchParams.toString();
  return asString ? `?${asString}` : '';
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = (body) => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = (body) => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = (body) => {
  return post('/api/transition-privileged', body);
};

// Fetch bookings for the current logged-in provider (includes customer profile images).
// If listingId is provided, filters by that specific listing.
// If listingId is not provided, returns all bookings for the current provider.
//
// See `server/api/get-bookings-by-listing.js` to see what data should
// be sent in the body.
export const getBookingsByListing = (body) => {
  return post('/api/get-bookings-by-listing', body);
};

export const getProviderBookingsMonthlyApi = (body) => {
  return post('/api/get-provider-bookings-monthly', body);
};

export const getCustomerBookingsMonthlyApi = (body) => {
  return post('/api/get-customer-bookings-monthly', body);
};

export const getProviderStatsApi = (body) => {
  return post('/api/get-provider-stats', body);
};

export const getProviderActivityApi = (body) => {
  return post('/api/get-provider-activity', body);
};

export const getProviderStatsHistoricalApi = (body) => {
  return post('/api/get-provider-stats-historical', body);
};

export const getProviderRevenueDetailsApi = (body) => {
  return post('/api/get-provider-revenue-details', body);
};

export const getProviderBookingsPaginatedApi = (body) => {
  return post('/api/get-provider-bookings-paginated', body);
};

export const getProviderCustomersApi = (body) => {
  return post('/api/get-provider-customers', body);
};

/** JSON: BookingData-backed coach bookings (optional; paginated POST endpoint also uses BookingData). */
export const getCoachBookingsApi = params => {
  const qs = createQueryString(params || {});
  return get(`/api/coach/bookings${qs}`, {
    headers: withDevUserHeader({ Accept: 'application/json' }),
  });
};

export const getCoachBookingsBySessionApi = (sessionTypeId, params) => {
  const qs = createQueryString(params || {});
  return get(`/api/coach/bookings/by-session/${encodeURIComponent(sessionTypeId)}${qs}`, {
    headers: withDevUserHeader({ Accept: 'application/json' }),
  });
};

export const getCoachCustomersFromBookingDataApi = params => {
  const qs = createQueryString(params || {});
  return get(`/api/coach/customers${qs}`, {
    headers: withDevUserHeader({ Accept: 'application/json' }),
  });
};

/** GET /api/coach/customers/:customerId/profile-preview */
export const getCoachCustomerProfilePreviewApi = customerId =>
  get(`/api/coach/customers/${encodeURIComponent(customerId)}/profile-preview`, {
    headers: withDevUserHeader({ Accept: 'application/json' }),
  });

/** GET /api/coach/customers/:customerId/reviews?page=&perPage= */
export const getCoachCustomerReviewsApi = (customerId, params = {}) => {
  const qs = createQueryString(params || {});
  return get(`/api/coach/customers/${encodeURIComponent(customerId)}/reviews${qs}`, {
    headers: withDevUserHeader({ Accept: 'application/json' }),
  });
};

/** GET /api/listings/:listingId/reviews?limit=&cursor= */
export const getListingReviewsApi = (listingId, params = {}) => {
  const qs = createQueryString(params || {});
  return get(`/api/listings/${encodeURIComponent(listingId)}/reviews${qs}`, {
    headers: withDevUserHeader({ Accept: 'application/json' }),
  });
};

/** GET /api/users/:subjectId/reviews?type=&limit=&cursor= */
export const getSubjectReviewsApi = (subjectId, params = {}) => {
  const qs = createQueryString(params || {});
  return get(`/api/users/${encodeURIComponent(subjectId)}/reviews${qs}`, {
    headers: withDevUserHeader({ Accept: 'application/json' }),
  });
};

/** JSON: customer BookingData in range (start/end ISO query params). */
export const getCustomerBookingsDataApi = params => {
  const qs = createQueryString(params || {});
  return get(`/api/customer/bookings${qs}`, {
    headers: withDevUserHeader({ Accept: 'application/json' }),
  });
};

export const getPendingCustomerReviewOpportunitiesApi = params => {
  const qs = createQueryString(params || {});
  return get(`/api/customer/review-opportunities/pending${qs}`, {
    headers: withDevUserHeader({ Accept: 'application/json' }),
  });
};

export const validateCustomerReviewOpportunityApi = (transactionId, params) => {
  const qs = createQueryString(params || {});
  return get(
    `/api/customer/review-opportunities/${encodeURIComponent(String(transactionId))}/validate${qs}`,
    {
      headers: withDevUserHeader({ Accept: 'application/json' }),
    }
  );
};

export const getPendingCoachReviewOpportunitiesApi = params => {
  const qs = createQueryString(params || {});
  return get(`/api/coach/review-opportunities/pending${qs}`, {
    headers: withDevUserHeader({ Accept: 'application/json' }),
  });
};

export const validateCoachReviewOpportunityApi = (transactionId, params) => {
  const qs = createQueryString(params || {});
  return get(`/api/coach/review-opportunities/${encodeURIComponent(String(transactionId))}/validate${qs}`, {
    headers: withDevUserHeader({ Accept: 'application/json' }),
  });
};

export const getCustomerReviewsReceivedApi = () =>
  get('/api/customer/reviews/received', {
    headers: withDevUserHeader({ Accept: 'application/json' }),
  });

export const getCustomerReviewsSubmittedApi = () =>
  get('/api/customer/reviews/submitted', {
    headers: withDevUserHeader({ Accept: 'application/json' }),
  });

export const getCoachReviewsReceivedApi = () =>
  get('/api/coach/reviews/received', {
    headers: withDevUserHeader({ Accept: 'application/json' }),
  });

export const getCoachReviewsSubmittedApi = () =>
  get('/api/coach/reviews/submitted', {
    headers: withDevUserHeader({ Accept: 'application/json' }),
  });

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = (body) => {
  return post('/api/auth/create-user-with-idp', body);
};

export const usersQueryApi = (body) => {
  return post('/api/users-query', body);
};
export const addToFavoritesApi = (body) => {
  return post('/api/add-to-favorites', body);
};

export const allusersQueryApi = (body) => {
  return post('/api/all-users-query', body);
};
export const CompleteTxn = (body) => {
  return post('/api/update-txn', body);
};

export const transactionTransitionApi = (body) => {
  return post('/api/transaction-transition', body);
};

export const payCancellationFineApi = (body) => {
  return post('/api/pay-cancellation-fine', body);
};

export const confirmStripePaymentApi = (body) => {
  return post('/api/confirm-stripe-payment', body);
};

export const clearCancellationFineApi = (body) => {
  return post('/api/clear-cancellation-fine', body);
};

export const getFavouritedUserEmailApi = (body) => {
  return post('/api/get-favourited-user-email', body);
};

export const updateTransactionMetadataApi = (body) => {
  return post('/api/update-transaction-metadata', body);
};

export const sendMessageNotificationApi = (body) => {
  return post('/api/send-message-notification', body);
};

//custom flow endpoints
export const createSchedulingBlockApi = (body) => {
  return post('/api/create-scheduling-block', body);
};
export const getSchedulingBlocksApi = (body) => {
  return post('/api/get-scheduling-blocks', body);
};
export const updateSchedulingBlockApi = (body) => {
  return post('/api/update-scheduling-block', body);
};
export const deleteSchedulingBlockApi = (body) => {
  return post('/api/delete-scheduling-block', body);
};

export const createSchedulededDateApi = (body) => {
  return post('/api/create-scheduleded-date', body);
};
export const getSchedulededDatesApi = (body) => {
  return post('/api/get-scheduleded-dates', body);
};
export const deleteSchedulededDateApi = (body) => {
  return post('/api/delete-scheduleded-date', body);
};

// User listing endpoints
export const createAutoListingApi = (body) => {
  return post('/api/create-auto-listing', body);
};

export const getUserListingApi = (body) => {
  return post('/api/get-user-listing', body);
};

export const getCoachByListingIdApi = (body) => {
  return post('/api/get-coach-by-listing-id', body);
};

export const getUserListingImagesApi = (body) => {
  return post('/api/get-user-listing-images', body);
};

// Camp endpoints
export const createCampApi = (body) => {
  return post('/api/create-camp', body);
};

export const getCampsApi = (body) => {
  return post('/api/get-camps', body);
};

export const getCampByIdApi = (body) => {
  return post('/api/get-camp-by-id', body);
};

export const updateCampApi = (body) => {
  return post('/api/update-camp', body);
};

export const deleteCampApi = (body) => {
  return post('/api/delete-camp', body);
};

// Session Type endpoints
export const createSessionTypeApi = (body) => {
  return post('/api/create-session-type', body);
};

export const updateSessionTypesSortOrderApi = (body) => {
  return post('/api/update-session-types-sort-order', body);
};

export const getSessionTypesApi = (body) => {
  return post('/api/get-session-types', body);
};

export const getSessionTypeByIdApi = (body) => {
  return post('/api/get-session-type-by-id', body);
};

export const updateSessionTypeApi = (body) => {
  return post('/api/update-session-type', body);
};

export const deleteSessionTypeApi = (body) => {
  return post('/api/delete-session-type', body);
};

export const getSessionTypesByListingApi = (body) => {
  return post('/api/get-session-types-by-listing', body);
};

export const getListingContentApi = (body = {}) => {
  return post('/api/get-listing-content', body);
};

export const updateListingContentApi = (body) => {
  return post('/api/update-listing-content', body);
};

export const getPublicSessionTypesApi = (body = {}) => {
  return post('/api/get-public-session-types', body);
};

export const getPublicCampsApi = (body = {}) => {
  return post('/api/get-public-camps', body);
};

// Fetch public session types by MongoDB document IDs.
// Used by Favorites → Listings, where favorites are stored as SessionType `_id` values.
export const getPublicSessionTypesByIdsApi = (body = {}) => {
  return post('/api/get-public-session-types-by-ids', body);
};

// Fetch a single session type for its own public detail page, keyed by the
// session type's own _id (not the coach's shared shell listingId, which
// every session type for a coach carries identically).
export const getSessionTypeDetailApi = (body = {}) => {
  return post('/api/get-session-type-detail', body);
};
export const searchDbListingsApi = (body = {}) => {
  return post('/api/search-db-listings', body);
};

// Refresh coach onboarding status after Stripe Connect hosted onboarding.
export const refreshCoachOnboardingApi = (body = {}) => {
  return post('/api/coach/onboarding/refresh', body);
};

// Discount code endpoints
export const createDiscountCodeApi = (body) => {
  return post('/api/create-discount-code', body);
};

export const getDiscountCodesApi = (body) => {
  return post('/api/get-discount-codes', body);
};

export const updateDiscountCodeApi = (body) => {
  return post('/api/update-discount-code', body);
};

export const deleteDiscountCodeApi = (body) => {
  return post('/api/delete-discount-code', body);
};

export const validateDiscountCodeApi = (body) => {
  return post('/api/validate-discount-code', body);
};

export const applyDiscountCodeApi = (body) => {
  return post('/api/apply-discount-code', body);
};

// Donation endpoints
export const createDonationCheckoutSessionApi = (body) => {
  return post('/api/donations/create-checkout-session', body);
};

export const getMyDonationsApi = (params) => {
  const qs = createQueryString(params || {});
  return get(`/api/donations/mine${qs}`, {
    headers: withDevUserHeader({ Accept: 'application/json' }),
  });
};

// This route has its own express.json() -- needs real JSON, not Transit.
export const updateDonationFulfillmentApi = (donationId, body) => {
  return patchJson(
    `/api/donations/${encodeURIComponent(String(donationId))}/fulfillment`,
    body,
    withDevUserHeader({})
  );
};

export const getMyReceiptsApi = () => {
  return get('/api/customer/receipts', {
    headers: withDevUserHeader({ Accept: 'application/json' }),
  });
};

export const getPublicDonationFeedApi = (coachId) => {
  return get(`/api/donations/public/${encodeURIComponent(String(coachId))}`);
};

// Instant sessions (coach-created on-the-spot sessions paid by QR). These
// routes use express.json(), so bodies go through postJson/patchJson.
const jsonAccept = () => ({ headers: withDevUserHeader({ Accept: 'application/json' }) });

export const listMyInstantSessionsApi = () => get('/api/coach/instant-sessions', jsonAccept());

export const createInstantSessionApi = body =>
  postJson('/api/coach/instant-sessions', body, withDevUserHeader({}));

export const getMyInstantSessionApi = id =>
  get(`/api/coach/instant-sessions/${encodeURIComponent(String(id))}`, jsonAccept());

export const updateMyInstantSessionApi = (id, body) =>
  patchJson(`/api/coach/instant-sessions/${encodeURIComponent(String(id))}`, body, withDevUserHeader({}));

export const updateInstantSessionPaymentContactApi = (id, paymentId, body) =>
  patchJson(
    `/api/coach/instant-sessions/${encodeURIComponent(String(id))}/payments/${encodeURIComponent(
      String(paymentId)
    )}`,
    body,
    withDevUserHeader({})
  );

export const getPublicInstantSessionApi = id =>
  get(`/api/instant-sessions/${encodeURIComponent(String(id))}/public`, { headers: { Accept: 'application/json' } });

export const createInstantSessionCheckoutApi = (id, body) =>
  postJson(`/api/instant-sessions/${encodeURIComponent(String(id))}/checkout`, body);

export const getInstantSessionReceiptApi = (id, checkoutSessionId) =>
  get(
    `/api/instant-sessions/${encodeURIComponent(String(id))}/receipt?cs=${encodeURIComponent(
      String(checkoutSessionId)
    )}`,
    { headers: { Accept: 'application/json' } }
  );

// QR check-in for booked sessions (record-only).
export const getBookingCheckInCodeApi = bookingId =>
  get(`/api/customer/bookings/${encodeURIComponent(String(bookingId))}/check-in-code`, jsonAccept());

export const checkInBookingApi = token =>
  postJson(`/api/coach/check-in/${encodeURIComponent(String(token))}`, {}, withDevUserHeader({}));

// Admin — per-coach donation commission overrides
export const listCoachCommissionOverridesApi = () => {
  return get('/api/admin/donation-commission-overrides', {
    headers: withDevUserHeader({ Accept: 'application/json' }),
  });
};

// This route has its own express.json() -- needs real JSON, not Transit.
export const setCoachCommissionOverrideApi = (body) => {
  return postJson('/api/admin/donation-commission-overrides', body, withDevUserHeader({}));
};

export const clearCoachCommissionOverrideApi = (coachId) => {
  return del(`/api/admin/donation-commission-overrides/${encodeURIComponent(String(coachId))}`, {
    headers: withDevUserHeader({ Accept: 'application/json' }),
  });
};

// Coach subscription tier endpoints
export const getCoachSubscriptionTiersApi = () => {
  return get('/api/coach/subscription-tiers');
};

export const createCoachSubscriptionTierApi = (body) => {
  return post('/api/coach/subscription-tiers', body);
};

export const getCoachSubscriptionTierByIdApi = (tierId) => {
  return get(`/api/coach/subscription-tiers/${tierId}`);
};

export const updateCoachSubscriptionTierApi = (tierId, body) => {
  return put(`/api/coach/subscription-tiers/${tierId}`, body);
};

export const deleteCoachSubscriptionTierApi = (tierId) => {
  return del(`/api/coach/subscription-tiers/${tierId}`);
};

export const toggleCoachSubscriptionTierActiveApi = (tierId, isActive) => {
  return patch(`/api/coach/subscription-tiers/${tierId}/toggle-active`, { isActive });
};

export const updateCoachSubscriptionTiersSortOrderApi = (body) => {
  return post('/api/coach/subscription-tiers/sort-order', body);
};

export const uploadSubscriptionTierImageApi = file => {
  const formData = new FormData();
  formData.append('image', file);

  const url = `${apiBaseUrl()}/api/coach/subscription-tiers/upload-image`;
  return window.fetch(url, {
    method: 'POST',
    credentials: 'include',
    body: formData,
    headers: withDevUserHeader(),
  }).then(res => {
    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);
        throw e;
      });
    }
    return res.json();
  });
};

export const uploadCoachCoverImageApi = file => {
  const formData = new FormData();
  formData.append('image', file);

  const url = `${apiBaseUrl()}/api/coach/profile/upload-cover-image`;
  return window.fetch(url, {
    method: 'POST',
    credentials: 'include',
    body: formData,
    headers: withDevUserHeader(),
  }).then(res => {
    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);
        throw e;
      });
    }
    return res.json();
  });
};

export const uploadProfileImageApi = file => {
  const formData = new FormData();
  formData.append('image', file);

  const url = `${apiBaseUrl()}/api/upload-profile-image`;
  return window.fetch(url, {
    method: 'POST',
    credentials: 'include',
    body: formData,
    headers: withDevUserHeader(),
  }).then(res => {
    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);
        throw e;
      });
    }
    return res.json();
  });
};

export const getCoachSubscriptionSummaryApi = () => {
  return get('/api/coach/subscriptions/summary');
};

export const getCoachCancellationStrikesSummaryApi = () => {
  return get('/api/coach/cancellation-strikes/summary');
};

export const getCoachSubscriptionProfileSettingsApi = () => {
  return get('/api/coach/subscription-profile-settings');
};

export const updateCoachSubscriptionProfileSettingsApi = body => {
  return patch('/api/coach/subscription-profile-settings', body, {
    headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }),
  });
};

export const getCoachSubscribersApi = () => {
  return get('/api/coach/subscribers');
};

export const getCoachPayoutAccountStatusApi = () => {
  return get('/api/coach/payout-account/status');
};

export const createCoachPayoutAccountOnboardingLinkApi = (body) => {
  if (!body || Object.keys(body).length === 0) {
    // post() with no body still defaults Content-Type to
    // 'application/transit+json' (see request()'s header default), and the
    // server's transit-deserialize middleware throws on an empty body
    // ("Invalid Transit in request body.") -- skip transit serialization
    // entirely rather than sending an empty one for the common no-override
    // case (already-a-coach managing their existing payout account).
    return post('/api/coach/payout-account/onboarding-link', undefined, { headers: {} });
  }
  // A brand-new (not-yet-a-coach) caller passes { returnUrl, refreshUrl } so
  // the Stripe onboarding redirect lands back on StripePayoutPage itself
  // (which already has the canAutoConvertToCoach/onRefreshCoachOnboarding
  // logic) instead of the default /provider destination meant for an
  // already-enabled coach managing their account.
  return post('/api/coach/payout-account/onboarding-link', body);
};

export const sendCoachSubscriberMessageApi = (subscriptionId, message) => {
  return post(
    `/api/coach/subscribers/${subscriptionId}/message`,
    { message },
    {
      headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }),
    }
  );
};

export const getCustomerSubscriptionsApi = () => {
  return get('/api/customer/subscriptions');
};

export const cancelCustomerSubscriptionApi = (subscriptionId, payload) => {
  return patch(`/api/customer/subscriptions/${subscriptionId}/cancel`, payload || {});
};

export const pauseCustomerSubscriptionApi = (subscriptionId, payload) => {
  return patch(`/api/customer/subscriptions/${subscriptionId}/pause`, payload || {});
};

export const resumeCustomerSubscriptionApi = subscriptionId => {
  return patch(`/api/customer/subscriptions/${subscriptionId}/resume`, {});
};

export const updateCustomerSubscriptionAutoRenewApi = (subscriptionId, autoRenew) => {
  const payload = { autoRenew };
  return patch(`/api/customer/subscriptions/${subscriptionId}/auto-renew`, payload);
};

export const getSubscriptionRefundEligibilityApi = subscriptionId =>
  fetchJson(`/api/customer/subscriptions/${subscriptionId}/refund-eligibility`, { method: 'GET' });

export const createSubscriptionRefundRequestApi = (subscriptionId, body) =>
  fetchJson(`/api/customer/subscriptions/${subscriptionId}/refund-request`, {
    method: 'POST',
    body,
  });

export const getPublicSubscriptionTiersByListingApi = (listingId) => {
  return get(`/api/subscription/tiers/by-listing/${listingId}`);
};

export const getPublicCoachSubscriptionProfileApi = coachId => {
  return get(`/api/coaches/${encodeURIComponent(coachId)}/subscription-profile`, {
    headers: { Accept: 'application/json' },
  });
};

export const getPublicSubscriptionsApi = (params = {}) => {
  const queryString = createQueryString(params);
  return get(`/api/public/subscriptions${queryString}`, {
    headers: { Accept: 'application/json' },
  });
};

export const createSubscriptionCheckoutSessionApi = (tierId, discountCode) => {
  return post('/api/subscription/create-checkout-session', {
    tierId,
    ...(discountCode ? { discountCode } : {}),
  });
};

// Coach-managed subscription-tier discount codes -- these routes have no
// express.json() of their own, so they're parsed by apiRouter's global
// Transit-body middleware. That only fires for an exact
// 'application/transit+json' Content-Type; 'Accept: application/json'
// alone doesn't request it, so post() silently skipped serializing the
// body and the server always saw an empty one. Matches the same header
// shape every other Transit-parsed route in this file already uses.
export const createSubscriptionDiscountCodeApi = body => {
  return post('/api/subscription/discount-codes', body, {
    headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }),
  });
};

export const getSubscriptionDiscountCodesApi = () => {
  return post(
    '/api/subscription/discount-codes/list',
    {},
    { headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }) }
  );
};

export const updateSubscriptionDiscountCodeApi = body => {
  return post('/api/subscription/discount-codes/update', body, {
    headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }),
  });
};

export const deleteSubscriptionDiscountCodeApi = discountCodeId => {
  return post(
    '/api/subscription/discount-codes/delete',
    { discountCodeId },
    { headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }) }
  );
};

export const validateSubscriptionDiscountCodeApi = ({ coachId, code, tierId }) => {
  return post('/api/subscription/discount-codes/validate', { coachId, code, tierId });
};

// Coach vanity profile URLs (/u/:slug) -- these routes DO have their own
// express.json(), so they need real JSON (postJson), not Transit.
export const resolveProfileSlugApi = slug => {
  return postJson('/api/profile/resolve-slug', { slug });
};

export const getSlugForUserApi = userId => {
  return postJson('/api/profile/slug-for-user', { userId });
};

export const setProfileSlugApi = slug => {
  return postJson('/api/profile/slug', { slug }, withDevUserHeader({}));
};

export const clearProfileSlugApi = () => {
  return del('/api/profile/slug', { headers: withDevUserHeader({ Accept: 'application/json' }) });
};

// A coach opting into a Discovery/Maps pin from their own dashboard (see
// GetPinnedOnMapButton.js) -- this route has its own express.json(), so it
// needs real JSON (postJson), not Transit.
export const connectToDiscoveryMapApi = ({ category, categoryLabel, location, lat, lng, bio }) => {
  return postJson('/api/discovery/connect-map', { category, categoryLabel, location, lat, lng, bio });
};

// Passwordless (magic-link) login request -- this route has its own
// express.json(), so it needs real JSON (postJson), not Transit. Always
// resolves (server responds success regardless of whether the email
// matches an account, by design -- see server/api/auth/passwordless.js).
export const requestPasswordlessLinkApi = ({ email, from, userType }) => {
  return postJson('/api/auth/passwordless/request', { email, from, userType });
};

// Phase 5 (native session): replaces the client's previous direct use of
// sdk.login()/sdk.currentUser.create()/.show()/.updateProfile()/sdk.logout().
// All native/* routes have their own express.json(), so real JSON
// (postJson/patchJson/get), not Transit -- see server/api/auth/nativeSession.js.
export const nativeLoginApi = ({ username, password }) => {
  return postJson('/api/auth/native/login', { username, password });
};

export const nativeSignupApi = params => {
  return postJson('/api/auth/native/signup', params);
};

export const nativeSetPasswordApi = ({ currentPassword, newPassword }) => {
  return postJson('/api/auth/native/set-password', { currentPassword, newPassword });
};

export const nativeLogoutApi = () => {
  return postJson('/api/auth/native/logout', {});
};

export const nativeRefreshApi = () => {
  return postJson('/api/auth/native/refresh', {});
};

export const nativeMeApi = () => {
  return get('/api/auth/native/me', { headers: { Accept: 'application/json' } });
};

export const nativeUpdateProfileApi = payload => {
  return patchJson('/api/auth/native/profile', payload);
};

// No express.json() of its own -- Transit-parsed like the discount-code
// functions above.
export const createGiftCheckoutSessionApi = body => {
  return post('/api/subscription/gifts/create-checkout-session', body, {
    headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }),
  });
};

// Coach network (team roster / connections) -- also Transit-parsed, no
// express.json() of their own.
const withDevUserTransitHeaders = () => withDevUserHeader({ 'Content-Type': 'application/transit+json' });

export const searchCoachesApi = query => {
  return post('/api/network/search-coaches', { query }, { headers: withDevUserTransitHeaders() });
};

export const getMyNetworkApi = () => {
  return post('/api/network/list', {}, { headers: withDevUserTransitHeaders() });
};

// GET, no body -- sidesteps the whole request-body-serialization class of
// bug entirely, and this one specifically must stay side-effect-free (see
// the comment on the matching server handler).
export const getNetworkUnseenCountApi = () => {
  return get('/api/network/unseen-count', { headers: withDevUserHeader({ Accept: 'application/json' }) });
};

export const inviteToNetworkApi = body => {
  return post('/api/network/invite', body, { headers: withDevUserTransitHeaders() });
};

export const respondToNetworkRequestApi = ({ connectionId, decision }) => {
  return post('/api/network/respond', { connectionId, decision }, { headers: withDevUserTransitHeaders() });
};

export const updateNetworkConnectionRoleApi = ({ connectionId, role }) => {
  return post('/api/network/update-role', { connectionId, role }, { headers: withDevUserTransitHeaders() });
};

export const removeNetworkConnectionApi = connectionId => {
  return post('/api/network/remove', { connectionId }, { headers: withDevUserTransitHeaders() });
};

export const getPublicTeamApi = coachId => {
  return get(`/api/network/public/${encodeURIComponent(String(coachId))}`);
};

export const joinNetworkViaLinkApi = ({ inviterCoachId, role }) => {
  return post(
    '/api/network/join-via-link',
    { inviterCoachId, role },
    { headers: withDevUserTransitHeaders() }
  );
};

export const createSubscriptionDiscussionApi = body => {
  return post('/api/subscription/discussions', body, {
    headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }),
  });
};

export const getSubscriptionDiscussionsApi = (params = {}) => {
  const queryString = createQueryString(params);
  return get(`/api/subscription/discussions${queryString}`, {
    headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }),
  });
};

export const getSubscriptionDiscussionByIdApi = discussionId => {
  return get(`/api/subscription/discussions/${discussionId}`, {
    headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }),
  });
};

export const updateSubscriptionDiscussionApi = (discussionId, body) => {
  return patch(`/api/subscription/discussions/${discussionId}`, body, {
    headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }),
  });
};

export const archiveSubscriptionDiscussionApi = discussionId => {
  return del(`/api/subscription/discussions/${discussionId}`, {
    headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }),
  });
};

export const deleteSubscriptionDiscussionApi = discussionId => {
  return archiveSubscriptionDiscussionApi(discussionId);
};

export const getCustomerGroupsApi = params => {
  const qs = createQueryString(params || {});
  return get(`/api/customer/groups${qs}`, {
    headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }),
  });
};

export const getCoachBookingGroupSourceOptionsApi = (params = {}) => {
  const qs = createQueryString(params || {});
  return get(`/api/coach/groups/source-options${qs}`, {
    headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }),
  });
};

export const postCoachBookingGroupMemberPreviewApi = body => {
  return post('/api/coach/groups/member-preview', body, {
    headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }),
  });
};

export const createCoachBookingGroupApi = body => {
  return post('/api/coach/groups', body, {
    headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }),
  });
};

export const getCoachBookingGroupsApi = params => {
  const qs = createQueryString(params || {});
  return get(`/api/coach/groups${qs}`, {
    headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }),
  });
};

export const getCoachBookingGroupByIdApi = id => {
  return get(`/api/coach/groups/${id}`, {
    headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }),
  });
};

export const updateCoachBookingGroupApi = (id, body) => {
  return patch(`/api/coach/groups/${id}`, body, {
    headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }),
  });
};

export const archiveCoachBookingGroupApi = id => {
  return del(`/api/coach/groups/${id}`, {
    headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }),
  });
};

export const createSubscriptionBroadcastApi = body => {
  return post('/api/subscription/broadcasts', body, {
    headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }),
  });
};

export const getSubscriptionBroadcastsApi = () => {
  return get('/api/subscription/broadcasts', {
    headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }),
  });
};

export const getSubscriptionBroadcastByIdApi = broadcastId => {
  return get(`/api/subscription/broadcasts/${broadcastId}`, {
    headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }),
  });
};

export const getCustomerBroadcastsApi = () => {
  return get('/api/customer/broadcasts', {
    headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }),
  });
};

export const getBookingBroadcastSourceOptionsApi = () => {
  return get('/api/coach/booking-broadcasts/source-options', {
    headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }),
  });
};

export const previewBookingBroadcastRecipientsApi = body => {
  return post('/api/coach/booking-broadcasts/preview-recipients', body, {
    headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }),
  });
};

export const createBookingBroadcastApi = body => {
  return post('/api/coach/booking-broadcasts', body, {
    headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }),
  });
};

export const getCoachBookingBroadcastsApi = () => {
  return get('/api/coach/booking-broadcasts', {
    headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }),
  });
};

export const getCustomerBookingBroadcastsApi = () => {
  return get('/api/customer/booking-broadcasts', {
    headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }),
  });
};

// Booking Data endpoints
export const saveCheckoutDataApi = (body) => {
  return post('/api/save-checkout-data', body);
};

export const getBookedTimeSlotsApi = (body) => {
  return post('/api/get-booked-time-slots', body);
};

export const validateBookingCreditsApi = (body) => {
  return post('/api/booking/validate-credits', body);
};

export const useBookingCreditsApi = (body) => {
  return post('/api/booking/use-credits', body);
};

/** Unified booking create (JSON). Handles credits server-side and booking thread. */
export const createBookingApi = body => {
  return request('/api/bookings', {
    method: 'POST',
    headers: withDevUserHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });
};

/** Order page payload for a BookingData row (credit bookings without Sharetribe transaction id). */
export const getCustomerBookingOrderApi = bookingId => {
  return request(`/api/customer/bookings/${encodeURIComponent(bookingId)}/order`, {
    method: 'GET',
    headers: withDevUserHeader({ 'Content-Type': 'application/json' }),
  });
};

// Phase 4 (Sharetribe payment-engine replacement), Stage 10: review
// submission for a BookingData-only (native-engine) booking -- see
// server/api/booking-review-submit.js. Addressed by the same bookingId
// getCustomerBookingOrderApi already uses, not a Sharetribe transaction id.
export const submitBookingDataReviewApi = (bookingId, body) => {
  return request(`/api/customer/bookings/${encodeURIComponent(bookingId)}/review`, {
    method: 'POST',
    headers: withDevUserHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });
};

// Phase 4 (Sharetribe payment-engine replacement), Stage 6: the native
// booking-checkout API. Unauthenticated boolean check, a PaymentIntent
// creation call, and a post-payment status poll -- see
// server/api/create-booking-payment-intent.js and its two sibling files.
export const getBookingCheckoutEligibilityApi = coachId => {
  const queryString = createQueryString({ coachId });
  return get(`/api/booking-checkout/eligibility${queryString}`, {
    headers: withDevUserHeader({ 'Content-Type': 'application/json' }),
  });
};

export const createBookingPaymentIntentApi = body => {
  return postJson('/api/booking-checkout/create-payment-intent', body, withDevUserHeader({}));
};

export const getBookingCheckoutStatusApi = bookingTransactionId => {
  return get(`/api/booking-checkout/${encodeURIComponent(bookingTransactionId)}/status`, {
    headers: withDevUserHeader({ 'Content-Type': 'application/json' }),
  });
};

export const acceptBookingApi = bookingId => {
  return request(`/api/bookings/${encodeURIComponent(bookingId)}/accept`, {
    method: 'PATCH',
    headers: withDevUserHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({}),
  });
};

export const rejectBookingApi = (bookingId, cancellationReason = '') => {
  return request(`/api/bookings/${encodeURIComponent(bookingId)}/reject`, {
    method: 'PATCH',
    headers: withDevUserHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ cancellationReason: cancellationReason || '' }),
  });
};

/** Customer order page: post into app booking thread (Coach Inbox → Booking Messages), not only Sharetribe. */
export const sendBookingThreadMessageByTransactionApi = (transactionId, message) => {
  return request(
    `/api/bookings/by-transaction/${encodeURIComponent(String(transactionId || '').trim())}/messages`,
    {
      method: 'POST',
      headers: withDevUserHeader({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ message }),
    }
  );
};

/** Fetch a native (BookingData-only) booking's own thread, by transactionId (bookingId). */
export const getBookingThreadMessagesByTransactionApi = transactionId => {
  return request(
    `/api/bookings/by-transaction/${encodeURIComponent(String(transactionId || '').trim())}/messages`,
    { headers: withDevUserHeader({ 'Content-Type': 'application/json' }) }
  );
};

/** Count of native bookings needing this coach's attention (new requests, cancellation-consent responses). */
export const getCoachNotificationCountApi = () => {
  return request('/api/coach/notifications/count', {
    headers: withDevUserHeader({ 'Content-Type': 'application/json' }),
  });
};

/** Count of native bookings needing this customer's attention (cancellation-consent responses). */
export const getCustomerNotificationCountApi = () => {
  return request('/api/customer/notifications/count', {
    headers: withDevUserHeader({ 'Content-Type': 'application/json' }),
  });
};

export const cancelCustomerBookingApi = (bookingId, payload = {}) => {
  return request(`/api/bookings/${encodeURIComponent(bookingId)}/cancel`, {
    method: 'PATCH',
    headers: withDevUserHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload || {}),
  });
};

export const requestBookingSupportApi = ({ bookingId, transactionId, source, message }) => {
  return request('/api/bookings/support-request', {
    method: 'POST',
    headers: withDevUserHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      bookingId: bookingId || null,
      transactionId: transactionId || null,
      source: source || null,
      message: message || null,
    }),
  });
};

export const coachCancelBookingApi = (bookingId, payload = {}) => {
  return request(`/api/bookings/${encodeURIComponent(bookingId)}/coach-cancel`, {
    method: 'PATCH',
    headers: withDevUserHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload || {}),
  });
};

export const coachCancelClassApi = (bookingId, payload = {}) => {
  return request('/api/bookings/cancel-class', {
    method: 'POST',
    headers: withDevUserHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      bookingId,
      ...(payload || {}),
    }),
  });
};

export const coachCancelClassPreviewApi = bookingId => {
  return request('/api/bookings/cancel-class/preview', {
    method: 'POST',
    headers: withDevUserHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ bookingId }),
  });
};

export const respondCoachCancellationRequestApi = (bookingId, payload = {}) => {
  return request(`/api/bookings/${encodeURIComponent(bookingId)}/coach-cancel/respond`, {
    method: 'PATCH',
    headers: withDevUserHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload || {}),
  });
};

export const respondCustomerCancellationRequestApi = (bookingId, payload = {}) => {
  return request(`/api/bookings/${encodeURIComponent(bookingId)}/customer-cancel/respond`, {
    method: 'PATCH',
    headers: withDevUserHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload || {}),
  });
};

// S3 endpoints
export const uploadImageApi = (formData) => {
  const url = `${apiBaseUrl()}/api/upload-image`;
  return window
    .fetch(url, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    })
    .then((res) => {
      if (res.status >= 400) {
        return res.json().then((data) => {
          let e = new Error();
          e = Object.assign(e, data);
          throw e;
        });
      }
      return res.json();
    });
};

export const deleteS3ImageApi = (body) => {
  return post('/api/delete-s3-image', body);
};

// Google Calendar sync endpoints
export const syncGoogleCalendarApi = (body) => {
  return post('/api/sync-google-calendar', body);
};

export const handleGoogleCalendarCallbackApi = (body) => {
  return post('/api/auth/google-calendar/callback', body);
};

// DEPRECATED - use startDirectThreadApi / sendThreadMessageApi
export const sendDirectMessageApi = (body) => {
  return post('/api/send-direct-message', body);
};
export const sendDirectMessageBatchApi = (body) => {
  return post('/api/send-direct-message-batch', body);
};

// DEPRECATED - use getThreadMessagesApi
export const getDirectMessagesApi = (body) => {
  return post('/api/get-direct-messages', body);
};

// DEPRECATED - use getDirectThreadsApi
export const getDirectMessageListApi = (body = {}) => {
  return post('/api/get-direct-message-list', body);
};

export const getDirectThreadsApi = () => {
  return get('/api/messages/direct/threads', {
    headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }),
  });
};

export const startDirectThreadApi = (recipientId, initialMessage) => {
  const body = {
    recipientId,
    ...(typeof initialMessage === 'string' ? { initialMessage } : {}),
  };
  return post('/api/messages/direct/start', body, {
    headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }),
  });
};

export const getBookingThreadsApi = (params = {}) => {
  const qs = createQueryString(params || {});
  return get(`/api/messages/booking/threads${qs}`, {
    headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }),
  });
};

export const startBookingThreadApi = body => {
  return post('/api/messages/booking/start', body, {
    headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }),
  });
};

export const getBookingThreadMessagesApi = (threadId, params = {}) => {
  const queryString = createQueryString(params);
  return get(`/api/messages/booking/${threadId}/messages${queryString}`, {
    headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }),
  });
};

export const getThreadMessagesApi = (threadId, params = {}) => {
  const queryString = createQueryString(params);
  return get(`/api/messages/threads/${threadId}/messages${queryString}`, {
    headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }),
  });
};

export const sendThreadMessageApi = (threadId, body, attachments = []) => {
  return post(
    `/api/messages/threads/${threadId}/messages`,
    { body, attachments },
    {
      headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }),
    }
  );
};

export const markThreadReadApi = threadId => {
  return patch(`/api/messages/threads/${threadId}/read`, {}, {
    headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }),
  });
};

export const getInboxConversationsSummaryApi = (params = {}) => {
  const role = params.role === 'coach' || params.role === 'customer' ? params.role : 'customer';
  const qs = createQueryString({
    role,
    ...(params.limit != null ? { limit: params.limit } : {}),
  });
  return get(`/api/messages/conversations/summary${qs}`, {
    headers: withDevUserHeader({ 'Content-Type': 'application/transit+json' }),
  });
};

// FAQ endpoints
export const getFAQsApi = (body) => {
  return post('/api/get-faqs', body);
};

export const getPublicFAQsApi = (body) => {
  return post('/api/get-public-faqs', body);
};

export const updateFAQsApi = (body) => {
  return post('/api/update-faqs', body);
};

// User profile endpoint with firstName and lastName
export const getUserWithNamesApi = (body) => {
  return post('/api/get-user-with-names', body);
};

// Landing Page Settings
export const getLandingPageSettingsApi = () => {
  return window
    .fetch(`${apiBaseUrl()}/api/landing-page-settings`, {
      method: 'GET',
      credentials: 'include',
    })
    .then((res) => {
      if (res.status >= 400) {
        return res.json().then((data) => {
          const e = new Error();
          throw Object.assign(e, data);
        });
      }
      return res.json();
    });
};

export const saveLandingPageSettingsApi = (body) => {
  return post('/api/landing-page-settings', body);
};

export const getLandingPageProvidersApi = (body) => {
  return post('/api/landing-page-providers', body);
};

export const getLandingPageUpcomingSessionsApi = (body) => {
  return post('/api/landing-page-upcoming-sessions', body);
};

export const getLandingPageSessionsByLocationApi = (body) => {
  return post('/api/landing-page-sessions-by-location', body);
};

export const getLandingPageNearbySessionsApi = (body) => {
  return post('/api/landing-page-nearby-sessions', body);
};

const fetchJson = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  return window
    .fetch(url, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
      ...options,
      body: options.body ? JSON.stringify(options.body) : undefined,
    })
    .then(res => {
      if (res.status >= 400) {
        return res.json().then(data => {
          const e = new Error(data?.error || 'Request failed');
          throw Object.assign(e, data);
        });
      }
      return res.json();
    });
};

export const getAdminAccessApi = () => fetchJson('/api/admin/access', { method: 'GET' });

export const createSupportTicketApi = body =>
  fetchJson('/api/support/tickets', { method: 'POST', body });

export const getMySupportTicketsApi = () =>
  fetchJson('/api/support/tickets/my', { method: 'GET' });

export const getSupportTicketApi = ticketId =>
  fetchJson(`/api/support/tickets/${ticketId}`, { method: 'GET' });

export const replySupportTicketApi = (ticketId, body) =>
  fetchJson(`/api/support/tickets/${ticketId}/messages`, { method: 'POST', body });

const buildQueryString = params => {
  const clean = Object.entries(params || {}).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      acc[key] = String(value);
    }
    return acc;
  }, {});
  const qs = new URLSearchParams(clean).toString();
  return qs ? `?${qs}` : '';
};

export const getAdminSupportTicketsApi = params =>
  fetchJson(`/api/admin/support/tickets${buildQueryString(params)}`, { method: 'GET' });

export const getAdminSupportTicketApi = ticketId =>
  fetchJson(`/api/admin/support/tickets/${ticketId}`, { method: 'GET' });

export const replyAdminSupportTicketApi = (ticketId, body) =>
  fetchJson(`/api/admin/support/tickets/${ticketId}/messages`, { method: 'POST', body });

export const updateAdminSupportTicketApi = (ticketId, body) =>
  fetchJson(`/api/admin/support/tickets/${ticketId}`, { method: 'PATCH', body });

export const getAdminSubscriptionRefundReviewApi = ticketId =>
  fetchJson(`/api/admin/subscription-refund-requests/${ticketId}/review`, { method: 'GET' });

export const approveAdminSubscriptionRefundApi = (ticketId, body) =>
  fetchJson(`/api/admin/subscription-refund-requests/${ticketId}/approve`, {
    method: 'POST',
    body,
  });

export const rejectAdminSubscriptionRefundApi = (ticketId, body) =>
  fetchJson(`/api/admin/subscription-refund-requests/${ticketId}/reject`, {
    method: 'POST',
    body,
  });

export const getActivePlatformSettingsApi = () =>
  fetchJson('/api/admin/platform-settings/active', { method: 'GET' });

export const getPlatformSettingsHistoryApi = () =>
  fetchJson('/api/admin/platform-settings/history', { method: 'GET' });

export const createPlatformSettingsVersionApi = body =>
  fetchJson('/api/admin/platform-settings/versions', { method: 'POST', body });

export const reconcileCancellationFeePaymentApi = body =>
  fetchJson('/api/admin/reconcile-cancellation-fine', { method: 'POST', body });

export const getSubscriptionCheckoutPricingPreviewApi = tierId =>
  fetchJson(`/api/subscription/checkout-pricing-preview?tierId=${encodeURIComponent(tierId)}`, {
    method: 'GET',
  });

// Admin "view as user" -- read-only debug surface, see AdminViewAsController.
export const searchViewAsUsersApi = query =>
  fetchJson(`/api/admin/view-as/search${buildQueryString({ q: query })}`, { method: 'GET' });

export const listViewAsCoachesApi = params =>
  fetchJson(`/api/admin/view-as/coaches${buildQueryString(params)}`, { method: 'GET' });

export const startViewAsSessionApi = targetUserId =>
  fetchJson('/api/admin/view-as/start', { method: 'POST', body: { targetUserId } });

export const endViewAsSessionApi = sessionToken =>
  fetchJson('/api/admin/view-as/end', { method: 'POST', body: { sessionToken } });

export const getViewAsProfileApi = targetUserId =>
  fetchJson(`/api/admin/view-as/${targetUserId}/profile`, { method: 'GET' });

export const getViewAsCoachOverviewApi = coachId =>
  fetchJson(`/api/admin/view-as/${coachId}/overview`, { method: 'GET' });

export const getViewAsCoachSessionTypesApi = coachId =>
  fetchJson(`/api/admin/view-as/${coachId}/session-types`, { method: 'GET' });

export const getViewAsCoachSubscriptionsApi = coachId =>
  fetchJson(`/api/admin/view-as/${coachId}/subscriptions`, { method: 'GET' });

export const getViewAsCoachDonationsApi = coachId =>
  fetchJson(`/api/admin/view-as/${coachId}/donations`, { method: 'GET' });

export const getViewAsCoachRevenueApi = (coachId, year) =>
  fetchJson(`/api/admin/view-as/${coachId}/revenue${buildQueryString({ year })}`, { method: 'GET' });

export const getViewAsCoachNetworkApi = coachId =>
  fetchJson(`/api/admin/view-as/${coachId}/network`, { method: 'GET' });

export const getViewAsCoachPayoutStatusApi = coachId =>
  fetchJson(`/api/admin/view-as/${coachId}/payout-status`, { method: 'GET' });

export const getViewAsCoachAvailabilityApi = coachId =>
  fetchJson(`/api/admin/view-as/${coachId}/availability`, { method: 'GET' });

export const getViewAsCoachPreviewApi = coachId =>
  fetchJson(`/api/admin/view-as/${coachId}/preview`, { method: 'GET' });

export const getViewAsUserReviewsApi = (userId, role) =>
  fetchJson(`/api/admin/view-as/${userId}/reviews${buildQueryString({ role })}`, { method: 'GET' });

export const getViewAsCustomerBookingsApi = customerId =>
  fetchJson(`/api/admin/view-as/${customerId}/bookings`, { method: 'GET' });

export const getViewAsCustomerSubscriptionsApi = customerId =>
  fetchJson(`/api/admin/view-as/${customerId}/subscriptions-purchased`, { method: 'GET' });

export const getViewAsCustomerCreditsApi = customerId =>
  fetchJson(`/api/admin/view-as/${customerId}/credits`, { method: 'GET' });

export const getViewAsCustomerReceiptsApi = customerId =>
  fetchJson(`/api/admin/view-as/${customerId}/receipts`, { method: 'GET' });

export const getViewAsCustomerFavoritesApi = customerId =>
  fetchJson(`/api/admin/view-as/${customerId}/favorites`, { method: 'GET' });
